import api from "@others/api.ts"
import i18n from "../../locales/i18n.ts"

import { Endpoints } from "@constants/Endpoints.tsx"
import { AppFunctions } from "@others/AppFunctions.ts"
import { IApiResponse } from "@interfaces/generic/IApiResponse.ts"
import { PostLoginAdapter } from "@adapters/PostLoginAdapter.ts"
import { IPostLoginBodyApi } from "@interfaces/IPostLoginBodyApi.ts"
import { IPostSignupBodyApi } from "@interfaces/IPostSignupBodyApi.ts"
import { AppConsoleMessages } from "@constants/AppConsoleMessages.ts"
import { AppLocalStorageKeys } from "@constants/AppLocalStorageKeys.ts"
import { IPostLoginResponseCli } from "@interfaces/IPostLoginResponseCli.ts"
import { AppAuthorizedProfiles } from "@constants/AppAuthorizedProfiles.ts"
import { IPostLoginResponseApi } from "@interfaces/IPostLoginResponseApi.ts"
import { IPostSignupResponseApi } from "@interfaces/IPostSignupResponseApi.ts"
import { IPostCheckEmailBodyApi } from "@interfaces/IPostCheckEmailBodyApi.ts"
import { PostResetPasswordAdapter } from "@adapters/PostResetPasswordAdapter.ts"
import { IPostResetPasswordBodyApi } from "@interfaces/IPostResetPasswordBodyApi.ts"
import { IPostCheckEmailResponseApi } from "@interfaces/IPostCheckEmailResponseApi.ts"
import { IPostResetPasswordResponseCli } from "@interfaces/IPostResetPasswordResponseCli.ts"
import { IPostResetPasswordResponseApi } from "@interfaces/IPostResetPasswordResponseApi.ts"
import { PostRequestResetPasswordAdapter } from "@adapters/PostRequestResetPasswordAdapter.ts"
import { IPostRequestResetPasswordBodyApi } from "@interfaces/IPostRequestResetPasswordBodyApi.ts"
import { IPostRequestResetPasswordResponseCli } from "@interfaces/IPostRequestResetPasswordResponseCli.ts"
import { IPostRequestResetPasswordResponseApi } from "@interfaces/IPostRequestResetPasswordResponseApi.ts"

const handleLogin = (data: IPostLoginResponseApi) => {
	const currentTime = Date.now()
	const expiresInMs = data.expires_in * 1000
	const expirationTime = currentTime + expiresInMs

	localStorage.setItem(AppLocalStorageKeys.TOKEN, data.token)

	localStorage.setItem(AppLocalStorageKeys.REFRESH_TOKEN, data.refreshToken)

	localStorage.setItem(
		AppLocalStorageKeys.TOKEN_EXPIRATION_TIME,
		expirationTime.toString()
	)
}

const login = async ({
	email,
	password,
}: IPostLoginBodyApi): Promise<IPostLoginResponseCli> => {
	return api
		.post<IApiResponse<IPostLoginResponseApi>>(Endpoints.login, {
			email,
			password,
		})
		.then((response) => {
			const { data } = response.data

			// Avoid membership users to login in this app
			if (data?.user?.profileType === AppAuthorizedProfiles.MEMBERSHIP) {
				throw {
					response: {
						data: {
							error: {
								code: "INVALID_CREDENTIALS",
								details: i18n.t("message_invalid_credentials"),
							},
						},
					},
				}
			}

			handleLogin(data)
			return PostLoginAdapter(response.data)
		})
		.catch((error) => {
			throw AppFunctions.getErrorApiDetail(error)
		})
}

const refreshToken = async (): Promise<void> => {
	try {
		const refreshToken = localStorage.getItem(
			AppLocalStorageKeys.REFRESH_TOKEN
		)

		if (!refreshToken) {
			throw new Error(AppConsoleMessages.error.no_refresh_token)
		}

		const response = await api.post<IApiResponse<IPostLoginResponseApi>>(
			Endpoints.refreshToken,
			{
				refreshToken,
			}
		)

		handleLogin(response.data.data)
	} catch {
		console.error(AppConsoleMessages.error.refresh_token_failed)
		AppFunctions.clearLoginDataFromLocalStorage()
		AppFunctions.goToLogin()
	}
}

const checkEmail = async ({
	email,
}: IPostCheckEmailBodyApi): Promise<IPostCheckEmailResponseApi> => {
	return api
		.post<IApiResponse<IPostCheckEmailResponseApi>>(Endpoints.checkEmail, {
			email,
		})
		.then((response) => {
			const { data } = response.data
			return data
		})
		.catch((error) => {
			throw AppFunctions.getErrorApiDetail(error)
		})
}

// TEST: Implement test for this EP
const signup = async (
	jsonBody: IPostSignupBodyApi
): Promise<IPostSignupResponseApi> => {
	return api
		.post<IApiResponse<IPostSignupResponseApi>>(Endpoints.signUp, jsonBody)
		.then((response) => {
			const { data } = response.data
			return data
		})
		.catch((error) => {
			throw AppFunctions.getErrorApiDetail(error)
		})
}

const requestResetPassword = async (
	jsonBody: IPostRequestResetPasswordBodyApi
): Promise<IPostRequestResetPasswordResponseCli> => {
	return api
		.post<IApiResponse<IPostRequestResetPasswordResponseApi>>(
			Endpoints.requestResetPassword,
			jsonBody
		)
		.then(({ data }) => {
			return PostRequestResetPasswordAdapter(data)
		})
		.catch((error) => {
			throw AppFunctions.getErrorApiDetail(error)
		})
}

const resetPassword = async (
	jsonBody: IPostResetPasswordBodyApi
): Promise<IPostResetPasswordResponseCli> => {
	return api
		.post<IApiResponse<IPostResetPasswordResponseApi>>(
			Endpoints.resetPassword,
			jsonBody
		)
		.then(({ data }) => {
			return PostResetPasswordAdapter(data)
		})
		.catch((error) => {
			throw AppFunctions.getErrorApiDetail(error)
		})
}

export default {
	login,
	refreshToken,
	checkEmail,
	signup,
	requestResetPassword,
	resetPassword,
}
